import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray
} from '@angular/cdk/drag-drop';
import { WorkflowCategory } from '../../../../models/workflow-category';
import { WorkflowService } from '../../../../services/workflow.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Utilities } from '../../../../services/utilities';
import { ModalConfirmComponent } from '../../../../components/system/modal-confirm/modal-confirm.component';

@Component({
  selector: 'wm-workflow-category-list',
  standalone: false,
  templateUrl: './workflow-category-list.component.html',
  styleUrl: './workflow-category-list.component.css'
})
export class WorkflowCategoryListComponent implements OnInit, OnDestroy {
  @Input() workflowCategories: WorkflowCategory[] = [];
  @Input() workflowId: string;
  @Input() categoriesEnabled: boolean = false;
  @Output() categoriesChanged = new EventEmitter<WorkflowCategory[]>();
  @Output() categoriesEnabledChanged = new EventEmitter<boolean>();

  @ViewChild('disableCategoriesModal', { static: false })
  disableCategoriesModal: ModalConfirmComponent;

  categories: WorkflowCategory[] = [];
  newCategoryForm: UntypedFormGroup;
  showNewCategoryForm = false;
  loading = false;
  checkboxEnabled: boolean = false;
  private subscriptions = new Subscription();

  constructor(
    private _workflowService: WorkflowService,
    private _formBuilder: UntypedFormBuilder,
    private _toastr: ToastrService
  ) {
    this.newCategoryForm = this._formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]]
    });
  }

  ngOnInit(): void {
    // Use input categories or initialize empty array, filter out any null/undefined values
    this.categories = (this.workflowCategories || []).filter(
      category => category != null
    );

    // Initialize checkbox state to match input
    this.checkboxEnabled = this.categoriesEnabled;

    // Add static categories if they don't exist and categories are enabled
    if (this.categoriesEnabled) {
      this.ensureStaticCategories();
    }

    // Sort categories by ordinal for display
    this.categories.sort((a, b) => (a?.ordinal || 0) - (b?.ordinal || 0));
  }

  dragDisabled(category: WorkflowCategory): boolean {
    return category?.isEditing || this.loading;
  }

  onCategoriesEnabledChange(enabled: boolean): void {
    if (!enabled) {
      // Show confirmation modal if disabling and custom categories exist
      // Reset checkbox to enabled state immediately
      setTimeout(() => {
        this.checkboxEnabled = true;
      }, 0);
      this.disableCategoriesModal.open();
    } else {
      this.checkboxEnabled = enabled;
      this.categoriesEnabled = enabled;
      this.categoriesEnabledChanged.emit(enabled);

      if (enabled) {
        // Add static categories when enabling
        this.ensureStaticCategories();
        this.categories.sort((a, b) => (a?.ordinal || 0) - (b?.ordinal || 0));
      }
    }
  }

  confirmDisableCategories(): void {
    // Remove all  categories
    if (this.categories.length > 0) {
      this.loading = true;
      const deletePromises = this.categories.map(category =>
        this._workflowService.deleteWorkflowCategory(category.id).toPromise()
      );

      Promise.all(deletePromises)
        .then(() => {
          // Remove custom categories from the array
          this.categories = [];
          this.checkboxEnabled = false;
          this.categoriesEnabled = false;
          this.categoriesEnabledChanged.emit(false);
          this.categoriesChanged.emit([...this.categories]);
          this._toastr.success(
            'Categories have been disabled and custom categories removed.'
          );
          this.loading = false;
          this.disableCategoriesModal.cancel();
        })
        .catch(error => {
          console.error('Failed to remove categories:', error);
          this._toastr.error(
            'Failed to remove some categories. Please try again.'
          );
          this.loading = false;
          this.disableCategoriesModal.cancel();
        });
    } else {
      // No custom categories, just disable
      this.checkboxEnabled = false;
      this.categoriesEnabled = false;
      this.categoriesEnabledChanged.emit(false);
      this.disableCategoriesModal.cancel();
    }
  }

  cancelDisableCategories(): void {
    // User cancelled the disable operation, ensure toggle stays enabled
    this.categoriesEnabled = true;
  }

  private hasCustomCategories(): boolean {
    return this.categories.some(c => !c.isStatic);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  showAddForm(): void {
    this.showNewCategoryForm = true;
    this.newCategoryForm.reset();
  }

  cancelAdd(): void {
    this.showNewCategoryForm = false;
    this.newCategoryForm.reset();
  }

  addCategory(): void {
    if (this.newCategoryForm.valid) {
      if (!this.workflowId) {
        this._toastr.error('Cannot create category: Workflow ID is required.');
        return;
      }

      const formValue = this.newCategoryForm.value;
      const newCategory = new WorkflowCategory({
        id: this.generateId(),
        name: formValue.name.trim(),
        workflowId: this.workflowId,
        ordinal: this.categories.length + 1
      });

      // Check if this is a new workflow (not yet saved)
      const isNewWorkflow =
        !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;

      if (isNewWorkflow) {
        // For new workflows, just add to local array without saving
        this.categories.push(newCategory);
        this.categoriesChanged.emit([...this.categories]);
        this.showNewCategoryForm = false;
        this.newCategoryForm.reset();
        this._toastr.success(
          `Category "${newCategory.name}" will be saved with the workflow.`
        );
      } else {
        // For existing workflows, save immediately
        this.loading = true;

        const saveSubscription = this._workflowService
          .saveWorkflowCategory(newCategory)
          .subscribe({
            next: (savedCategory: WorkflowCategory) => {
              this.categories.push(savedCategory);
              this.categoriesChanged.emit([...this.categories]);
              this.showNewCategoryForm = false;
              this.newCategoryForm.reset();
              this._toastr.success(
                `Category "${savedCategory.name}" has been created successfully.`
              );
              this.loading = false;
            },
            error: error => {
              console.error('Failed to save category:', error);
              this._toastr.error(
                'Failed to create category. Please try again.'
              );
              this.loading = false;
            }
          });

        this.subscriptions.add(saveSubscription);
      }
    }
  }

  editCategory(category: WorkflowCategory): void {
    if (!category) {
      this._toastr.error('Invalid category selected for editing.');
      return;
    }
    category.originalName = category.name;
    category.isEditing = true;
  }

  saveCategory(category: WorkflowCategory): void {
    if (!category) {
      this._toastr.error('Invalid category data.');
      return;
    }

    if (category.name && category.name.trim()) {
      const isNewWorkflow =
        !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;

      if (isNewWorkflow) {
        // For new workflows, just update locally
        category.isEditing = false;
        this.categoriesChanged.emit([...this.categories]);
        this._toastr.success(
          `Category "${category.name}" will be saved with the workflow.`
        );
      } else {
        // For existing workflows, save immediately
        this.loading = true;

        const saveSubscription = this._workflowService
          .saveWorkflowCategory(category)
          .subscribe({
            next: (updatedCategory: WorkflowCategory) => {
              const index = this.categories.findIndex(
                c => c.id === updatedCategory.id
              );
              if (index > -1) {
                this.categories[index] = {
                  ...updatedCategory,
                  isEditing: false
                };
              }
              this.categoriesChanged.emit([...this.categories]);
              this._toastr.success(
                `Category "${updatedCategory.name}" has been updated successfully.`
              );
              this.loading = false;
            },
            error: error => {
              console.error('Failed to update category:', error);
              this._toastr.error(
                'Failed to update category. Please try again.'
              );
              this.loading = false;
            }
          });

        this.subscriptions.add(saveSubscription);
      }
    }
  }

  cancelEdit(category: WorkflowCategory): void {
    if (!category) {
      return;
    }
    category.name = category.originalName || category.name;
    category.isEditing = false;
    // Could reload original values here if needed
  }

  deleteCategory(category: WorkflowCategory): void {
    if (!category || !category.id || !category.name) {
      this._toastr.error('Invalid category selected for deletion.');
      return;
    }

    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;

    if (isNewWorkflow) {
      // For new workflows, just remove from local array
      const index = this.categories.findIndex(c => c.id === category.id);
      if (index > -1) {
        this.categories.splice(index, 1);
      }
      this.categoriesChanged.emit([...this.categories]);
      this._toastr.success(`Category "${category.name}" removed.`);
    } else {
      // For existing workflows, delete from database
      this.loading = true;

      const deleteSubscription = this._workflowService
        .deleteWorkflowCategory(category.id)
        .subscribe({
          next: (success: boolean) => {
            // if (success) {
            const index = this.categories.findIndex(c => c.id === category.id);
            if (index > -1) {
              this.categories.splice(index, 1);
            }
            this.categoriesChanged.emit([...this.categories]);
            this._toastr.success(
              `Category "${category.name}" has been deleted successfully.`
            );
            this.loading = false;
          },
          error: error => {
            console.error('Failed to delete category:', error);
            this._toastr.error('Failed to delete category. Please try again.');
            this.loading = false;
          }
        });

      this.subscriptions.add(deleteSubscription);
    }
  }

  onDrop(event: CdkDragDrop<WorkflowCategory[]>): void {
    if (
      event.previousIndex !== event.currentIndex &&
      this.categories &&
      this.categories.length > 0
    ) {
      // Filter out any null categories before processing
      this.categories = this.categories.filter(category => category != null);

      // Reorder the categories array
      moveItemInArray(this.categories, event.previousIndex, event.currentIndex);

      // Update ordinal values based on new positions
      this.categories.forEach((category, index) => {
        if (category) {
          category.ordinal = index + 1;
        }
      });

      // Save all categories with updated ordinals
      this.saveCategoriesOrder();

      // Emit the updated categories
      this.categoriesChanged.emit([...this.categories]);
    }
  }

  private saveCategoriesOrder(): void {
    if (!this.categories || this.categories.length === 0) {
      return;
    }

    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;

    if (isNewWorkflow) {
      // For new workflows, just emit the changes without saving
      this._toastr.success(
        'Category order updated. Changes will be saved with the workflow.'
      );
      return;
    }

    // For existing workflows, save to database
    this.loading = true;

    // Filter out null categories, save all valid categories (including static ones) with updated ordinals
    const validCategories = this.categories.filter(
      category => category != null && category.id
    );
    const savePromises = validCategories.map(category =>
      this._workflowService.saveWorkflowCategory(category).toPromise()
    );

    if (savePromises.length === 0) {
      this.loading = false;
      return;
    }

    Promise.all(savePromises)
      .then(() => {
        this._toastr.success('Category order updated successfully.');
        this.loading = false;
      })
      .catch(error => {
        console.error('Failed to update category order:', error);
        this._toastr.error(
          'Failed to update category order. Please try again.'
        );
        this.loading = false;
      });
  }

  trackByFn(index: number, item: WorkflowCategory): any {
    return item?.id || index;
  }

  private generateId(): string {
    return Utilities.generateId();
  }

  private ensureStaticCategories(): void {
    const hasAttachments = this.categories.some(
      c => c.staticCategoryType === 'attachments'
    );
    const hasNotes = this.categories.some(
      c => c.staticCategoryType === 'notes'
    );
    const hasSubmit = this.categories.some(
      c => c.staticCategoryType === 'submit'
    );

    const categoriesToAdd: WorkflowCategory[] = [];

    // Add Attachments category if it doesn't exist
    if (!hasAttachments) {
      const attachmentsCategory = new WorkflowCategory({
        id: this.generateId(),
        name: 'Attachments',
        workflowId: this.workflowId,
        ordinal: this.categories.length + 1,
        isStatic: true,
        staticCategoryType: 'attachments'
      });
      this.categories.push(attachmentsCategory);
      categoriesToAdd.push(attachmentsCategory);
    }

    // Add Notes category if it doesn't exist
    if (!hasNotes) {
      const notesCategory = new WorkflowCategory({
        id: this.generateId(),
        name: 'Notes',
        workflowId: this.workflowId,
        ordinal: this.categories.length + 1,
        isStatic: true,
        staticCategoryType: 'notes'
      });
      this.categories.push(notesCategory);
      categoriesToAdd.push(notesCategory);
    }

    // Add Submit category if it doesn't exist
    if (!hasSubmit) {
      const submitCategory = new WorkflowCategory({
        id: this.generateId(),
        name: 'Submit',
        workflowId: this.workflowId,
        ordinal: this.categories.length + 1,
        isStatic: true,
        staticCategoryType: 'submit'
      });
      this.categories.push(submitCategory);
      categoriesToAdd.push(submitCategory);
    }

    // Check if this is a new workflow
    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;

    // Save the static categories to the database only if workflow already exists
    if (categoriesToAdd.length > 0 && !isNewWorkflow) {
      this.loading = true;
      const savePromises = categoriesToAdd.map(category =>
        this._workflowService.saveWorkflowCategory(category).toPromise()
      );

      Promise.all(savePromises)
        .then(savedCategories => {
          // Update the categories in the array with the saved versions
          savedCategories.forEach((savedCategory, index) => {
            const categoryIndex = this.categories.findIndex(
              c => c.id === categoriesToAdd[index].id
            );
            if (categoryIndex > -1) {
              this.categories[categoryIndex] = savedCategory;
            }
          });
          this.categoriesChanged.emit([...this.categories]);
          this.loading = false;
        })
        .catch(error => {
          console.error('Failed to save static categories:', error);
          this._toastr.error(
            'Failed to save system categories. Please try again.'
          );
          this.loading = false;
        });
    } else if (categoriesToAdd.length > 0) {
      // For new workflows, just emit the changes
      this.categoriesChanged.emit([...this.categories]);
    }
  }
}
